import { V2L3SyncModal } from "@fusion-advantage/formrender"
import { useAppSelector, useAppDispatch } from "../../../redux/hooks"
import { AppModal, dismissAppModal, setSimulateOffline } from "../../../redux/appSlice"
import { useCallback, useMemo } from "react"
import { V2L1ProgressBarSegment, V2L1ProgressBarSegmentType, SyncModalItem } from "@fusion-advantage/formrender"
import { shortGuid, SyncItemStatus, syncItemStatusIsError, syncItemStatusIsSuccess, WorkItemType } from "@fusion-advantage/formlib"
import { syncItemIsCloudToDevice, syncItemIsDeviceToCloud, SyncItemType } from "../../../redux/syncthunk/SyncItemResult"
import { appSync } from "../../../redux/syncthunk/syncThunk"
import { SyncType } from "../../../redux/syncthunk/SyncConfig"
import { selectDashboardDisplayIdentity } from "../../../redux/dashboardDataSlice"

export const LxSyncModal = () => {
	const loginLdng = useAppSelector(state => Boolean(state.app.ldng.login))
	const displayIdentity = useAppSelector(selectDashboardDisplayIdentity)
	const syncing = useAppSelector(state => Boolean(state.sync.startedAt && !state.sync.endedAt))
	const items = useAppSelector(state => state.sync.syncItems)
	const denominatorEstimate = useAppSelector(state => state.sync.denominatorEstimate)
	const notice = useAppSelector(state => state.sync.notice)
	const simulateOffline = useAppSelector(state => state.app.simulateOffline)
	const dispatch = useAppDispatch()
	const progressSegments = useMemo((): V2L1ProgressBarSegment[] => {
		let segments: V2L1ProgressBarSegment[] = []
		let amount = 1 / Math.max(items.length, denominatorEstimate)
		for(let item of items) {
			if(syncItemStatusIsSuccess(item.result.status)) {
				segments.push({
					type: V2L1ProgressBarSegmentType.Success,
					amount,
				})
				continue
			}
			if(syncItemStatusIsError(item.result.status)) {
				// forget segments; turn the whole bar red
				return [{
					type: V2L1ProgressBarSegmentType.Error,
					amount: 1,
				}]
			}
			// pending; leave space on bar
		}
		return segments
	}, [items, denominatorEstimate])
	const downloadItems = useMemo((): SyncModalItem[] => {
		// flip expression to allow unknowns to pass through
		const filtered = items.filter(item => !syncItemIsDeviceToCloud(item.key))
		return filtered.map(item => {
			switch(item.key.type) {
				case SyncItemType.AppStatus:
					return {
						name: "App Status",
						status: item.result.status,
					}
				case SyncItemType.FrontendUpdate:
					return {
						name: "App Content Update",
						status: item.result.status,
					}
				case SyncItemType.EtagTest:
					return {
						name: "ETag Test",
						status: item.result.status,
					}
				case SyncItemType.OfflineTable:
					return {
						name: 'Table ' + item.key.tableId,
						status: item.result.status,
					}
				case SyncItemType.Todo:
					return {
						name: 'Todos',
						status: item.result.status,
					}
				case SyncItemType.Appt:
					return {
						name: 'Appointments',
						status: item.result.status,
					}
				case SyncItemType.ReplaceApptEncounters:
					return {
						name: 'Appt/Enc Map',
						status: item.result.status,
					}
				case SyncItemType.Export:
					switch(item.key.workItemKey.type) {
						case WorkItemType.Todo:
							return {
								name: 'Export ' + shortGuid(item.key.workItemKey.encounterGuid),
								status: item.result.status,
							}
						case WorkItemType.Appointment:
							return {
								name: 'Export ' + (item.key.sfApptNum ?? item.key.workItemKey.serviceApptId),
								status: item.result.status,
							}
					}
				case SyncItemType.Branch:
					return {
						name: 'Branch ' + shortGuid(item.key.branchGuid),
						status: item.result.status,
					}
				case SyncItemType.Renderer:
					return {
						name: `Renderer ${item.key.rendererNumber}`,
						status: item.result.status,
					}
				case SyncItemType.CloudToDeviceFile:
					return {
						name: 'File ' + item.key.fileId.slice(0, 8),
						status: item.result.status,
					}
				case SyncItemType.WriteCloudToDevice:
					return {
						name: 'DB Writes',
						status: item.result.status,
					}
				default:
					return {
						name: `Unknown ${item.key.type}`,
						status: item.result.status,
					}
			}
		})
	}, [items])
	const uploadItems = useMemo((): SyncModalItem[] => {
		// flip expression to allow unknowns to pass through
		const filtered = items.filter(item => !syncItemIsCloudToDevice(item.key))
		const mapped = filtered.map(item => {
			switch(item.key.type) {
				case SyncItemType.BatchDeviceToCloud:
					return {
						name: "Batch Uploads",
						status: item.result.status,
					}
				case SyncItemType.EncounterStatus:
					return {
						name: `Status ${item.key.ordinal} ${shortGuid(item.key.encounterGuid)}`,
						status: item.result.status,
					}
				case SyncItemType.Import:
					return {
						name: `Import ${item.key.ordinal} ${shortGuid(item.key.encounterGuid)}`,
						status: item.result.status,
					}
				case SyncItemType.DeviceToCloudFile:
					return {
						name: 'File ' + item.key.fileId.slice(0, 8),
						status: item.result.status,
					}
				case SyncItemType.ImportProcessingAllowance:
					return {
						name: 'Process Allowance',
						status: item.result.status,
					}
				default:
					return {
						name: `Unknown ${item.key.type}`,
						status: item.result.status,
					}
			}
		})
		// if only successful batch task and no real uploads,
		// hide the section
		if(mapped.length === 1 && mapped[0].status === SyncItemStatus.Ok) {
			return []
		}
		return mapped
	}, [items])

	const handleOnSyncClick = useCallback(() => {
		// must be online for sync
		if(simulateOffline) {
			if(!confirm('Currently simulating offline— restore connectivity and sync?')) {
				return
			}
			dispatch(setSimulateOffline(false))
		}
		dispatch(appSync({type: SyncType.Complete}))
	}, [simulateOffline, dispatch])

	return (
		<V2L3SyncModal
			syncing={syncing}
			progressSegments={progressSegments}
			progressDenominator={progressSegments.length}
			downloadItems={downloadItems}
			uploadItems={uploadItems}
			notice={notice}
			canSyncNow={Boolean(displayIdentity)}
			onSyncClick={handleOnSyncClick}
			canDismiss={Boolean(displayIdentity) && !loginLdng}
			onDismissClick={() => dispatch(dismissAppModal(AppModal.Sync))}
		/>
	)
}
